import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { playChime } from "../lib/chime";
import { FlowerOfLife, ChokuRei } from "../components/SacredGeometry";
import { Printer, ArrowLeft } from "lucide-react";

export default function Certificate() {
  const { courseId } = useParams();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.post(`/certificate/${courseId}`)
      .then((r) => { setCert(r.data); playChime(); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [courseId]);

  if (error) {
    return (
      <div className="min-h-screen pt-32 grid place-items-center bg-sanctuary-deep">
        <div className="text-center max-w-md px-6">
          <h1 className="font-display text-3xl text-parchment mb-3">Not yet, dear one</h1>
          <p className="text-text-muted mb-8">{error}</p>
          <Link to={`/courses/${courseId}`} className="px-7 py-3 rounded-full bg-gold text-sanctuary-deep font-medium">Return to the teachings</Link>
        </div>
      </div>
    );
  }

  if (!cert) return <div className="min-h-screen pt-32 text-center text-text-muted">Inscribing your seal…</div>;

  return (
    <div className="min-h-screen pt-28 pb-24 bg-sanctuary-deep">
      <style>{`@media print { body { background: #fff; } .no-print { display: none !important; } .cert-print { box-shadow: none !important; border-color: #b8912f !important; } }`}</style>
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8 no-print">
          <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-2 text-text-muted hover:text-gold text-sm transition-colors" data-testid="certificate-back">
            <ArrowLeft size={14} /> Back
          </Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors" data-testid="print-certificate-button">
            <Printer size={16} /> Print / Save as PDF
          </button>
        </div>

        <div className="cert-print relative rounded-2xl p-10 sm:p-16 text-center overflow-hidden gold-glow" style={{ background: "#F3EFE6", border: "2px solid #b8912f" }} data-testid="certificate">
          <FlowerOfLife className="absolute -top-16 -right-16 w-64 text-[#b8912f]/10" />
          <FlowerOfLife className="absolute -bottom-16 -left-16 w-64 text-[#b8912f]/10" />
          {/* corner brackets */}
          <span className="absolute top-5 left-5 w-8 h-8 border-t-2 border-l-2 border-[#b8912f]/50" />
          <span className="absolute top-5 right-5 w-8 h-8 border-t-2 border-r-2 border-[#b8912f]/50" />
          <span className="absolute bottom-5 left-5 w-8 h-8 border-b-2 border-l-2 border-[#b8912f]/50" />
          <span className="absolute bottom-5 right-5 w-8 h-8 border-b-2 border-r-2 border-[#b8912f]/50" />

          <div className="relative">
            <ChokuRei className="w-14 mx-auto text-[#b8912f] mb-5" />
            <div className="font-jp text-[#b8912f] text-xl tracking-[0.3em] mb-2">臼井靈氣療法</div>
            <div className="text-[#8a6d28] text-xs uppercase tracking-[0.3em] mb-8">Reiki Sanctuary · Certificate of Attunement</div>

            <p className="text-[#5a4a22] mb-3">This sacred seal is bestowed upon</p>
            <h1 className="font-display text-5xl text-[#2a2212] mb-3" data-testid="certificate-name">{cert.student_name}</h1>
            <p className="text-[#5a4a22] mb-2">having devotedly completed the teachings and attunement of</p>
            <h2 className="font-display text-3xl text-[#7a5f1e] italic mb-8">{cert.level_label} — {cert.course_title}</h2>

            <div className="flex items-center justify-center gap-8 text-xs text-[#6a5528] mb-10">
              <div>
                <div className="uppercase tracking-widest mb-1">Attuned on</div>
                <div className="font-display text-base text-[#2a2212]">{cert.date}</div>
              </div>
              <div className="w-px h-10 bg-[#b8912f]/40" />
              <div>
                <div className="uppercase tracking-widest mb-1">Seal Code</div>
                <div className="font-display text-base text-[#2a2212]" data-testid="certificate-seal">{cert.seal_code}</div>
              </div>
            </div>

            <div className="text-[10px] text-[#6a5528] leading-relaxed mb-6">
              Lineage · Mikao Usui → Chujiro Hayashi → Hawayo Takata → Reiki Sanctuary
            </div>

            <div className="inline-grid place-items-center w-20 h-20 rounded-full border-2 border-[#b8912f] text-[#b8912f] mx-auto rotate-6">
              <span className="font-jp text-2xl">霊</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#8a6d28] mt-2">Sanctuary Seal · Hanko</div>
          </div>
        </div>
      </div>
    </div>
  );
}
